<script setup lang="ts">
import SeverityBadge from "./SeverityBadge.vue";
import { eventFinalSeverity, type QualityEvent } from "../api";

const props = defineProps<{ event: QualityEvent }>();
defineEmits<{ (e: "open", eventId: number): void }>();

function relTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function decisionSummary(): string {
  const cs = props.event.contributors;
  if (cs.length === 0) return "no contributors";
  if (cs.length === 1) {
    const c = cs[0];
    const name = c.contributor.split("@")[0];
    if (c.contributor_decision === null) return `awaiting ${name}`;
    if (c.contributor_decision === "agree_with_comment")
      return `${name}: agreed (with comment)`;
    return `${name}: ${c.contributor_decision}`;
  }
  let agreed = 0;
  let pending = 0;
  let disputed = 0;
  for (const c of cs) {
    if (c.contributor_decision === null) pending++;
    else if (c.contributor_decision === "dispute") disputed++;
    else agreed++;
  }
  const parts: string[] = [];
  if (agreed) parts.push(`${agreed} agreed`);
  if (pending) parts.push(`${pending} pending`);
  if (disputed) parts.push(`${disputed} disputed`);
  return parts.join(", ");
}
</script>

<template>
  <button
    type="button"
    class="event-row"
    :data-event-id="event.id"
    :data-testid="'event-row-' + event.id"
    @click="$emit('open', event.id)"
  >
    <SeverityBadge
      :severity="event.severity"
      :final-severity="eventFinalSeverity(event)"
      class="row-sev"
    />
    <div class="row-main">
      <div class="row-summary">
        <span class="row-id" :data-testid="'qe-id-' + event.id">QE-{{ event.id }}</span>
        <span class="row-summary-text">{{ event.summary }}</span>
      </div>
      <div class="row-context">
        <span class="status" :data-status="event.status">{{ event.status }}</span>
        <span class="ctx-sep">·</span>
        <span>{{ event.category }}</span>
        <span class="ctx-sep">·</span>
        <span>reporter: {{ event.reporter || "—" }}</span>
        <span class="ctx-sep">·</span>
        <span>{{ relTime(event.date) }}</span>
        <span class="ctx-sep">·</span>
        <span class="decisions">{{ decisionSummary() }}</span>
      </div>
    </div>
    <i class="pi pi-chevron-right row-chevron"></i>
  </button>
</template>

<style scoped>
.event-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: border-color 120ms, box-shadow 120ms, transform 80ms;
}
.event-row + .event-row { margin-top: 6px; }
.event-row:hover {
  border-color: var(--p-primary-400);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.event-row:active { transform: scale(0.998); }
.event-row:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}
.row-sev { flex-shrink: 0; margin-top: 2px; }
.row-main { flex: 1; min-width: 0; }
.row-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--p-surface-900);
  min-width: 0;
}
.row-summary-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.row-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 600;
  color: var(--p-surface-600);
  background: var(--p-surface-100);
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.row-context {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 2px;
  font-size: 12px;
  flex-wrap: wrap;
  color: var(--p-surface-500);
}
.ctx-sep { color: var(--p-surface-400); }
.status {
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.04em;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  background: var(--p-surface-100);
  color: var(--p-surface-600);
}
.status[data-status="closed"] { background: #d1fae5; color: #065f46; }
.status[data-status="disputed"] { background: #fecaca; color: #7f1d1d; }
.decisions { color: var(--p-surface-500); }
.row-chevron {
  color: var(--p-surface-400);
  font-size: 12px;
  margin-top: 4px;
}
</style>
